import { QueryClient, type QueryClientConfig } from '@tanstack/react-query';

import { isApiError } from '@/lib/api';

const MAX_RETRIES = 2;

/**
 * Retrying a 4xx only burns the p95 budget (RNF04): the request is wrong, not unlucky. Network
 * failures and 5xx/429 are the only ones worth a second attempt.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRIES) {
    return false;
  }
  if (isApiError(error)) {
    return error.isRetryable;
  }
  return true;
}

export const queryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      // Catalog and order data change on the order of minutes, not seconds; a short fresh window
      // removes most refetches during a purchase session (RNF08: repeat order in <= 4 taps).
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      retry: shouldRetry,
      retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 8_000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      throwOnError: false,
    },
    mutations: {
      // A mutation may not be idempotent, so it is never retried automatically.
      retry: false,
    },
  },
};

export function createQueryClient(): QueryClient {
  return new QueryClient(queryClientConfig);
}
